import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [status, setStatus] = useState("checking");
  const polls = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let active = true;
    const poll = async () => {
      if (polls.current >= 6) {
        if (active) setStatus("timeout");
        return;
      }
      polls.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setStatus("success");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (active) setStatus("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, clearCart]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-28 text-center" data-testid="checkout-success-page">
      {status === "checking" && (
        <div data-testid="checkout-checking">
          <Loader2 className="w-14 h-14 text-[#D32F2F] mx-auto animate-spin" />
          <h1 className="mt-6 font-display font-black text-3xl text-[#262626]">Confirming your payment…</h1>
          <p className="mt-2 text-[#262626]/70">Hang tight, this only takes a moment.</p>
        </div>
      )}
      {status === "success" && (
        <div data-testid="checkout-paid">
          <CheckCircle2 className="w-16 h-16 text-[#D32F2F] mx-auto" />
          <h1 className="mt-6 font-display font-black text-4xl text-[#262626]">Thank you for your order!</h1>
          <p className="mt-3 text-[#262626]/70">Your payment was successful. We'll get your cards packed and shipped from Galt, CA. A confirmation will follow shortly.</p>
          <Link to="/products" className="inline-block mt-8 bg-[#D32F2F] text-white font-bold px-8 py-3.5 rounded-md hover:bg-[#B71C1C] transition-colors">Keep Shopping</Link>
        </div>
      )}
      {["error", "timeout", "expired"].includes(status) && (
        <div data-testid="checkout-error">
          <XCircle className="w-16 h-16 text-[#262626]/40 mx-auto" />
          <h1 className="mt-6 font-display font-black text-3xl text-[#262626]">
            {status === "expired" ? "Payment session expired" : "We couldn't confirm your payment"}
          </h1>
          <p className="mt-3 text-[#262626]/70">If you were charged, please email sales@hometowneracards.com and we'll sort it out right away.</p>
          <Link to="/products" className="inline-block mt-8 border-2 border-[#262626] text-[#262626] font-bold px-8 py-3.5 rounded-md hover:bg-[#262626] hover:text-[#F9F6F0] transition-colors">Back to Shop</Link>
        </div>
      )}
    </div>
  );
}
